// pages/Ec2OpenSearchView.jsx — EC2 Health → view "OpenSearch"
// Backend: GET /api/ec2-health/opensearch?account=&hours=  (backend/opensearchHealth.js) — read-only OpenSearch API + CloudWatch AWS/ES.
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { TrendChart } from './Ec2HealthHistory';

const RANGES = [[6, '6 h'], [24, '24 h'], [72, '3 d'], [168, '7 d'], [336, '14 d']];
const VERDICT = {
  critical: { label: 'Critical', pill: 'bg-red-100 text-red-700 ring-red-200' },
  warning: { label: 'Warning', pill: 'bg-amber-100 text-amber-700 ring-amber-200' },
  ok: { label: 'Healthy', pill: 'bg-emerald-100 text-emerald-700 ring-emerald-200' },
};
const STATUS = {
  green: 'bg-emerald-500', yellow: 'bg-amber-400', red: 'bg-red-500', unknown: 'bg-gray-300',
};
const LEVEL_CLS = { critical: 'text-red-700', warning: 'text-amber-700', info: 'text-gray-500' };
const n = (v, d = 0) => (v == null ? '–' : Number(v).toLocaleString('en-US', { maximumFractionDigits: d }));
const ms = (v) => (v == null ? '–' : v === 0 ? '0 ms' : v < 0.001 ? '< 0.001 ms' : v < 1 ? `${v.toFixed(3)} ms` : `${n(v, 1)} ms`);
const mb = (v) => (v == null ? '–' : v < 1024 ? `${n(v, 1)} MB` : `${n(v / 1024, 1)} GB`);

function Pill({ cls, children }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${cls}`}>{children}</span>;
}

function Stat({ label, value, sub, warn }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${warn ? 'text-amber-600' : 'text-gray-900'}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
}

function Domain({ d, days }) {
  const m = d.metrics || {};
  const v = (k) => (m[k] ? m[k].value : null);
  const pts = (k) => (m[k] ? m[k].points : []);
  const ver = VERDICT[d.verdict] || VERDICT.ok;
  const u = d.utilisation || {};
  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${STATUS[d.status] || STATUS.unknown}`} title={`Cluster ${d.status}`} />
        <h3 className="text-base font-semibold text-gray-900">{d.name}</h3>
        <Pill cls={ver.pill}>{ver.label}</Pill>
        <span className="text-xs text-gray-500">
          {d.engine} · {d.cluster.count}× {d.cluster.type}{d.cluster.dedicatedMaster ? ` + master ${d.cluster.masterType}` : ''}
          {' · '}{d.cluster.zoneAwareness ? `${d.cluster.azCount} AZ` : 'single AZ'} · {d.storage.type} {d.storage.sizeGb} GB/node
        </span>
        {d.processing && <span className="text-xs text-sky-600">config change in progress</span>}
      </div>
      <div className="truncate text-[11px] text-gray-400">{d.endpoint}</div>

      {d.alerts && d.alerts.length > 0 && (
        <ul className="space-y-0.5 text-xs">
          {d.alerts.map((a) => <li key={a.text} className={LEVEL_CLS[a.level]}>• {a.text}</li>)}
        </ul>
      )}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Cluster" value={d.status} sub={`${n(v('nodes'))} / ${n(d.cluster.count)} nodes`} warn={d.status !== 'green'} />
        <Stat label="CPU peak" value={`${n(v('cpu'))}%`} warn={v('cpu') >= 80} />
        <Stat label="JVM pressure" value={`${n(v('jvm'))}%`} sub="alert ≥ 80%" warn={v('jvm') >= 80} />
        <Stat label="Disk used" value={mb(d.storage.usedMb)} sub={`of ${n(d.storage.totalGb)} GB · ${d.storage.usedPct == null ? '–' : n(d.storage.usedPct, 2)}%`} warn={d.storage.usedPct >= 75} />
        <Stat label="Documents" value={n(v('docs'))} sub="searchable" />
        <Stat label="HTTP 5xx / 4xx" value={`${n(v('e5xx'))} / ${n(v('e4xx'))}`} warn={v('e5xx') > 0} />
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Stat label="Searches / day" value={n(u.searchesPerDay)} />
        <Stat label="Search latency" value={ms(v('searchLat'))} />
        <Stat label="Index ops / day" value={n(u.indexPerDay)} />
        <Stat label="Index latency" value={ms(v('indexLat'))} />
      </div>

      {u.lowUse && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
          Low utilisation: CPU under 15% and under 5% of storage used in this range — the provisioned size is well above current load.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="mb-1 text-xs font-medium text-gray-500">CPU & JVM pressure (%)</div>
          <TrendChart days={days} unit="%" height={120} series={[
            { name: 'CPU max', color: '#0ea5e9', points: pts('cpu') },
            { name: 'JVM pressure', color: '#8b5cf6', points: pts('jvm') },
          ]} />
        </div>
        <div>
          <div className="mb-1 text-xs font-medium text-gray-500">Search & indexing operations</div>
          <TrendChart days={days} unit="ops" height={120} series={[
            { name: 'Search', color: '#10b981', points: pts('searchRate') },
            { name: 'Indexing', color: '#f59e0b', points: pts('indexRate'), dashed: true },
          ]} />
        </div>
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-gray-500">Configuration review</div>
        {d.findings.length === 0 ? <div className="text-xs text-emerald-700">No configuration issues found.</div> : (
          <ul className="space-y-0.5 text-xs">
            {d.findings.map((f) => (
              <li key={f.text} className={LEVEL_CLS[f.level]}>
                <span className="mr-1 font-semibold uppercase">{f.level}</span>{f.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function Ec2OpenSearchView({ account }) {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((refresh) => {
    setLoading(true); setErr(null);
    api.get(`/api/ec2-health/opensearch?account=${account}&hours=${hours}${refresh ? '&refresh=1' : ''}`)
      .then((r) => setData(r.data)).catch((e) => setErr((e.response && e.response.data && e.response.data.error) || e.message))
      .finally(() => setLoading(false));
  }, [account, hours]);
  useEffect(() => { load(false); }, [load]);

  const domains = (data && data.domains) || [];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-lg bg-slate-100/80 p-0.5 text-xs">
          {RANGES.map(([h, l]) => (
            <button key={h} type="button" onClick={() => setHours(h)}
              className={`rounded-md px-2.5 py-1 font-medium ${hours === h ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
          {data && <span>Updated {new Date(data.generatedAt).toLocaleTimeString()}</span>}
          <button type="button" onClick={() => load(true)} disabled={loading}
            className="rounded-md border border-gray-200 bg-white px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>
      {err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {!err && data && domains.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-400">No OpenSearch domains in this account.</div>
      )}
      {domains.map((d) => <Domain key={d.name} d={d} days={hours / 24} />)}
      <div className="text-[11px] leading-relaxed text-gray-400">
        Read-only from AWS (OpenSearch Describe + CloudWatch AWS/ES); the cluster endpoint itself is not queried.
        Search/index counts are totals over the range divided per day; cached for 2 minutes.
      </div>
    </div>
  );
}
